import { router } from '@inertiajs/react';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface DeleteTeamButtonProps {
  eventSlug: string;
  teamId: string;
  teamSlug: string | null;
  memberCount: number;
  hasProject: boolean;
}

export function DeleteTeamButton({
  eventSlug,
  teamId,
  teamSlug,
  memberCount,
  hasProject,
}: DeleteTeamButtonProps) {
  const [isPending, setIsPending] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const handleDelete = () => {
    setIsPending(true);
    router.delete(`/admin/${eventSlug}/teams/${teamId}`, {
      onError: (errors) => {
        const failure = errors as unknown as Record<string, string | undefined>;
        toast({
          type: 'error',
          description: failure.base ?? 'Failed to delete team',
        });
      },
      onFinish: () => {
        setIsPending(false);
        setShowDialog(false);
      },
    });
  };

  return (
    <>
      <Button
        variant="outline"
        className="text-red-600 hover:text-red-700"
        onClick={() => setShowDialog(true)}
        disabled={isPending}
      >
        <Trash2 className="mr-2 h-4 w-4" />
        Delete team
      </Button>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {teamSlug ?? 'this team'}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>This cannot be undone.</p>
                {memberCount > 0 && (
                  <p>
                    The {memberCount} {memberCount === 1 ? 'member' : 'members'}{' '}
                    of this team will be left without a team. Their accounts and
                    applications are not deleted.
                  </p>
                )}
                {hasProject && (
                  <p>
                    The team&apos;s project, its submissions and its
                    presentation uploads will be deleted too.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {isPending ? 'Deleting...' : 'Delete team'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
